<template>
    <div v-if="show" class="modal-overlay" @click.self="close">
        <div class="modal-content">
            <div class="modal-header">
                <h3>Скачать видеофрагмент</h3>
                <button @click="close" class="close-btn">
                    <span class="mdi mdi-close"></span>
                </button>
            </div>

            <div class="modal-body">
                <!-- Existing Jobs List -->
                <div v-if="existingJobs.length > 0" class="existing-jobs-section">
                    <div class="section-title">Доступные записи</div>
                    <div class="jobs-list" ref="jobsList">
                        <div v-for="job in existingJobs" :key="job.Uuid" class="job-item"
                            :class="{ 'job-processing': job.Status !== 'Completed' }">
                            <div class="job-info">
                                <div class="job-name">{{ formatJobName(job) }}</div>
                                <div class="job-details-mini">
                                    <span v-if="job.TotalDuration">{{ job.TotalDuration.toFixed(0) }}с</span>
                                    <span v-if="job.Status !== 'Completed'" class="job-status">{{ job.Status }} - {{
                                        (job.Progress).toFixed(0) }}%</span>
                                </div>
                            </div>
                            <a v-if="job.Status === 'Completed'" :href="getDownloadUrl(job.Name)" target="_blank"
                                class="job-download-btn" title="Скачать">
                                <span class="mdi mdi-download"></span>
                            </a>
                        </div>
                    </div>
                    <div class="section-divider"></div>
                </div>

                <div class="time-selection">
                    <div class="section-title">Создать новую запись</div>

                    <div class="date-selector-wrapper">
                        <label class="date-label">Дата:</label>
                        <div class="date-selector">
                            <button 
                                class="date-nav-btn" 
                                @click="previousDay" 
                                :disabled="!hasPreviousDay"
                                title="Предыдущий день"
                            >
                                <span class="mdi mdi-chevron-left"></span>
                            </button>
                            
                            <button class="date-display-btn" @click="toggleDatePicker">
                                <span class="mdi mdi-calendar"></span>
                                <span>{{ formattedDate }}</span>
                            </button>
                            
                            <button 
                                class="date-nav-btn" 
                                @click="nextDay" 
                                :disabled="!hasNextDay"
                                title="Следующий день"
                            >
                                <span class="mdi mdi-chevron-right"></span>
                            </button>
                            
                            <!-- Date picker dropdown -->
                            <div v-if="showDatePicker" class="date-picker" ref="datePickerEl">
                                <div 
                                    v-for="day in props.availableDays" 
                                    :key="day.dateString"
                                    class="date-option"
                                    :class="{ active: day.dateString === selectedDay }"
                                    @click="selectDate(day.dateString)"
                                >
                                    {{ formatDateOption(day.dateString) }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="time-inputs">
                        <div class="input-group">
                            <label>С:</label>
                            <input type="text" v-model="fromTimeString" @input="handleFromTimeInput"
                                @keydown="handleTimeKeydown" placeholder="00:00:00" class="time-input-masked"
                                maxlength="8" />
                        </div>

                        <div class="input-group">
                            <label>По:</label>
                            <input type="text" v-model="toTimeString" @input="handleToTimeInput"
                                @keydown="handleTimeKeydown" placeholder="23:59:59" class="time-input-masked"
                                maxlength="8" />
                        </div>
                    </div>

                    <!-- Visual timeline showing available segments -->
                    <div class="segments-timeline">
                        <div 
                            class="segments-container" 
                            ref="timelineContainerEl"
                            @mousedown="onTimelineClick"
                        >
                            <template v-if="isAdmin">
                                <div 
                                    v-for="(segment, idx) in displaySegments" 
                                    :key="idx"
                                    class="segment-bar"
                                    :style="{
                                        left: `${segment.left}%`,
                                        width: `${segment.width}%`
                                    }"
                                ></div>
                            </template>
                            <div 
                                v-else-if="nonAdminSegmentBar"
                                class="segment-bar"
                                :style="{
                                    left: `${nonAdminSegmentBar.left}%`,
                                    width: `${nonAdminSegmentBar.width}%`
                                }"
                            ></div>
                            <!-- Selected range overlay -->
                            <div 
                                v-if="isValidRange"
                                class="selected-range"
                                :style="{
                                    left: `${selectedRangeLeft}%`,
                                    width: `${selectedRangeWidth}%`
                                }"
                            ></div>
                            <!-- Start time handle -->
                            <div 
                                class="time-handle time-handle-start"
                                :style="{ left: `${selectedRangeLeft}%` }"
                                @mousedown.stop="startDragHandle('from', $event)"
                                @touchstart.stop="startDragHandle('from', $event)"
                                :title="`Начало: ${fromTimeString}`"
                            >
                                <div class="handle-line"></div>
                                <div class="handle-grip">
                                    <span class="mdi mdi-drag-vertical"></span>
                                </div>
                            </div>
                            <!-- End time handle -->
                            <div 
                                class="time-handle time-handle-end"
                                :style="{ left: `${selectedRangeLeft + selectedRangeWidth}%` }"
                                @mousedown.stop="startDragHandle('to', $event)"
                                @touchstart.stop="startDragHandle('to', $event)"
                                :title="`Конец: ${toTimeString}`"
                            >
                                <div class="handle-line"></div>
                                <div class="handle-grip">
                                    <span class="mdi mdi-drag-vertical"></span>
                                </div>
                            </div>
                        </div>
                        <div class="time-markers">
                            <span>00:00</span>
                            <span>06:00</span>
                            <span>12:00</span>
                            <span>18:00</span>
                            <span>24:00</span>
                        </div>
                    </div>

                    <div class="duration-display">
                        ⏱️ Длительность: {{ durationText }}
                    </div>

                    <!-- Coverage warning -->
                    <div v-if="isAdmin && isValidRange && coverageInfo.hasGaps" class="warning-message">
                        <span class="mdi mdi-alert"></span>
                        {{ coverageInfo.message }}
                    </div>

                    <div v-if="error" class="error-message">
                        {{ error }}
                    </div>
                </div>

            </div>

            <div class="modal-footer">
                <button @click="startTranscode" class="btn btn-primary"
                    :disabled="!isValidRange">
                    Начать обработку
                </button>
                <button @click="close" class="btn btn-secondary">
                    Отмена
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, useTemplateRef, nextTick } from 'vue';
import axios from 'axios';
import { useDefaultStore } from '@/store';

const store = useDefaultStore();
const isAdmin = computed(() => store.isAdmin);
const props = defineProps({
    show: Boolean,
    url: String,
    cameraId: String,
    currentDay: String,
    availableDays: Array,
    allSegments: Array,
    serverTz: String,
    initialFromSeconds: Number,
    initialToSeconds: Number
});

const emit = defineEmits(['close']);

const selectedDay = ref('');
const showDatePicker = ref(false);
const datePickerEl = useTemplateRef('datePickerEl');
const fromTimeString = ref('00:00:00');
const toTimeString = ref('23:59:59');
const error = ref('');
const existingJobs = ref([]);
let eventSource = null;
const jobsList = useTemplateRef('jobsList');
const timelineContainerEl = useTemplateRef('timelineContainerEl');

// Handle dragging state
const draggingHandle = ref(null); // 'from' or 'to' or null

// Track previous job count for smooth scrolling
let previousJobCount = 0;

// Watch for new jobs and scroll to bottom
watch(() => existingJobs.value.length, async (newLength) => {
    if (newLength > previousJobCount && jobsList.value) {
        // Wait for DOM update
        await nextTick();
        // Smooth scroll to bottom
        jobsList.value.scrollTo({
            top: jobsList.value.scrollHeight,
            behavior: 'smooth'
        });
    }
    previousJobCount = newLength;
});

// Convert seconds to HH:MM:SS string
function secondsToTimeString(seconds) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Convert HH:MM:SS string to seconds
function timeStringToSeconds(timeStr) {
    const parts = timeStr.split(':');
    if (parts.length !== 3) return 0;
    const h = parseInt(parts[0]) || 0;
    const m = parseInt(parts[1]) || 0;
    const s = parseInt(parts[2]) || 0;
    return Math.min(86399, Math.max(0, h * 3600 + m * 60 + s));
}

// Mask input to HH:MM:SS format
function maskTimeInput(value) {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    if (digits.length === 0) return '';
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4, 6)}`;
}

function handleFromTimeInput(e) {
    const cursorPos = e.target.selectionStart;
    const oldValue = fromTimeString.value;
    const newValue = maskTimeInput(e.target.value);
    fromTimeString.value = newValue;

    // Restore cursor position
    if (newValue.length >= oldValue.length) {
        const newPos = cursorPos + (newValue.length - oldValue.length);
        e.target.setSelectionRange(newPos, newPos);
    }
}

function handleToTimeInput(e) {
    const cursorPos = e.target.selectionStart;
    const oldValue = toTimeString.value;
    const newValue = maskTimeInput(e.target.value);
    toTimeString.value = newValue;

    // Restore cursor position
    if (newValue.length >= oldValue.length) {
        const newPos = cursorPos + (newValue.length - oldValue.length);
        e.target.setSelectionRange(newPos, newPos);
    }
}

function handleTimeKeydown(e) {
    // Allow: backspace, delete, tab, escape, enter, and navigation keys
    if ([46, 8, 9, 27, 13].indexOf(e.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true) ||
        // Allow: home, end, left, right
        (e.keyCode >= 35 && e.keyCode <= 39)) {
        return;
    }

    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
        e.preventDefault();
    }
}

// Computed values for seconds
const fromSeconds = computed(() => timeStringToSeconds(fromTimeString.value));
const toSeconds = computed(() => timeStringToSeconds(toTimeString.value));

// Initialize times and date when modal opens
watch(() => props.show, (newVal) => {
    if (newVal) {
        // Initialize selected date with current day
        selectedDay.value = props.currentDay;
        
        if (props.initialFromSeconds !== undefined) {
            fromTimeString.value = secondsToTimeString(props.initialFromSeconds);
        }
        if (props.initialToSeconds !== undefined) {
            toTimeString.value = secondsToTimeString(props.initialToSeconds);
        }
        error.value = '';
        existingJobs.value = [];
        previousJobCount = 0; // Reset job count when modal opens

        // Connect to SSE to get existing jobs
        connectToSSE();
    } else {
        // Close SSE when modal closes
        if (eventSource) {
            eventSource.close();
            eventSource = null;
        }
        showDatePicker.value = false;
    }
});

const isValidRange = computed(() => {
    return fromSeconds.value < toSeconds.value;
});

const durationText = computed(() => {
    if (!isValidRange.value) return 'Неверный диапазон';
    const duration = toSeconds.value - fromSeconds.value;
    const h = Math.floor(duration / 3600);
    const m = Math.floor((duration % 3600) / 60);
    const s = Math.floor(duration % 60);

    if (h > 0) {
        return `${h}ч ${m}м ${s}с`;
    } else if (m > 0) {
        return `${m}м ${s}с`;
    } else {
        return `${s}с`;
    }
});

const formattedDate = computed(() => {
    if (!selectedDay.value) return '';
    // selectedDay is in format yyyy-MM-dd
    const [year, month, day] = selectedDay.value.split('-');
    return `${day}.${month}.${year}`;
});

// Get segments for the selected day
const daySegments = computed(() => {
    if (!props.allSegments || !selectedDay.value) return [];
    
    const [year, month, day] = selectedDay.value.split('-');
    const dayStart = new Date(year, month - 1, day);
    dayStart.setHours(0, 0, 0, 0);
    const dayStartMs = dayStart.getTime();
    const dayEndMs = dayStartMs + 86400000; // 24 hours
    
    return props.allSegments.filter(seg => {
        // Include segment if it overlaps with the selected day
        return seg.startMs < dayEndMs && seg.endMs > dayStartMs;
    }).map(seg => {
        // Convert segment to seconds from start of day
        const startMs = Math.max(seg.startMs, dayStartMs);
        const endMs = Math.min(seg.endMs, dayEndMs);
        return {
            startSeconds: Math.floor((startMs - dayStartMs) / 1000),
            endSeconds: Math.floor((endMs - dayStartMs) / 1000)
        };
    });
});

// Calculate display segments as percentages of the day
const displaySegments = computed(() => {
    return daySegments.value.map(seg => ({
        left: (seg.startSeconds / 86400) * 100,
        width: ((seg.endSeconds - seg.startSeconds) / 86400) * 100
    }));
});

// For non-admin users: single solid bar from first segment start to last segment end
const nonAdminSegmentBar = computed(() => {
    if (daySegments.value.length === 0) return null;
    const firstStart = Math.min(...daySegments.value.map(s => s.startSeconds));
    const lastEnd = Math.max(...daySegments.value.map(s => s.endSeconds));
    return {
        left: (firstStart / 86400) * 100,
        width: ((lastEnd - firstStart) / 86400) * 100
    };
});

// Calculate selected range position
const selectedRangeLeft = computed(() => {
    return (fromSeconds.value / 86400) * 100;
});

const selectedRangeWidth = computed(() => {
    if (!isValidRange.value) return 0;
    return ((toSeconds.value - fromSeconds.value) / 86400) * 100;
});

// Check coverage of selected range
const coverageInfo = computed(() => {
    if (!isValidRange.value || daySegments.value.length === 0) {
        return { 
            hasGaps: true, 
            coverage: 0,
            message: 'Видео для выбранного дня отсутствует'
        };
    }
    
    const from = fromSeconds.value;
    const to = toSeconds.value;
    
    // Find all segments that overlap with selected range
    const overlappingSegments = daySegments.value.filter(seg => 
        seg.startSeconds < to && seg.endSeconds > from
    );
    
    if (overlappingSegments.length === 0) {
        return {
            hasGaps: true,
            coverage: 0,
            message: 'В выбранном диапазоне нет доступного видео'
        };
    }
    
    // Calculate total coverage
    let coveredSeconds = 0;
    let sortedSegments = overlappingSegments.sort((a, b) => a.startSeconds - b.startSeconds);
    
    for (const seg of sortedSegments) {
        const segStart = Math.max(seg.startSeconds, from);
        const segEnd = Math.min(seg.endSeconds, to);
        coveredSeconds += (segEnd - segStart);
    }
    
    const totalSeconds = to - from;
    const coveragePercent = (coveredSeconds / totalSeconds) * 100;
    
    if (coveragePercent < 100) {
        const missingSeconds = totalSeconds - coveredSeconds;
        const missingMinutes = Math.ceil(missingSeconds / 60);
        return {
            hasGaps: true,
            coverage: coveragePercent,
            message: `Видео доступно только на ${coveragePercent.toFixed(0)}% выбранного диапазона (пропущено ~${missingMinutes} мин.)`
        };
    }
    
    return {
        hasGaps: false,
        coverage: 100,
        message: ''
    };
});

// Date navigation helpers
const currentDayIndex = computed(() => {
    if (!props.availableDays || !selectedDay.value) return -1;
    return props.availableDays.findIndex(d => d.dateString === selectedDay.value);
});

const hasPreviousDay = computed(() => {
    return currentDayIndex.value > 0;
});

const hasNextDay = computed(() => {
    return currentDayIndex.value >= 0 && currentDayIndex.value < props.availableDays.length - 1;
});

function previousDay() {
    if (hasPreviousDay.value) {
        selectedDay.value = props.availableDays[currentDayIndex.value - 1].dateString;
    }
}

function nextDay() {
    if (hasNextDay.value) {
        selectedDay.value = props.availableDays[currentDayIndex.value + 1].dateString;
    }
}

function toggleDatePicker() {
    showDatePicker.value = !showDatePicker.value;
}

function selectDate(dateString) {
    selectedDay.value = dateString;
    showDatePicker.value = false;
}

function formatDateOption(dateString) {
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    const dayName = date.toLocaleDateString('ru', { weekday: 'short' });
    return `${dayName}, ${day}.${month}.${year}`;
}

// Close date picker when clicking outside
function handleClickOutside(event) {
    if (datePickerEl.value && !datePickerEl.value.contains(event.target) && 
        !event.target.closest('.date-display-btn')) {
        showDatePicker.value = false;
    }
}

// Watch for date picker visibility
watch(showDatePicker, (show) => {
    if (show) {
        document.addEventListener('click', handleClickOutside);
    } else {
        document.removeEventListener('click', handleClickOutside);
    }
});

// Timeline handle dragging
function startDragHandle(handle, event) {
    if (!timelineContainerEl.value) return;
    
    draggingHandle.value = handle;
    
    // Add global listeners
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup', onDragEnd);
    document.addEventListener('touchmove', onDragMove);
    document.addEventListener('touchend', onDragEnd);
    
    // Prevent text selection
    event.preventDefault();
}

function onDragMove(event) {
    if (!draggingHandle.value || !timelineContainerEl.value) return;
    
    const clientX = event.type.startsWith('touch') ? event.touches[0].clientX : event.clientX;
    const rect = timelineContainerEl.value.getBoundingClientRect();
    
    // Calculate position as percentage of timeline
    const positionX = clientX - rect.left;
    const percentX = Math.max(0, Math.min(100, (positionX / rect.width) * 100));
    
    // Convert to seconds (0-86399)
    let newSeconds = Math.floor((percentX / 100) * 86400);
    newSeconds = Math.max(0, Math.min(86399, newSeconds));
    
    // Update the appropriate time
    if (draggingHandle.value === 'from') {
        // Ensure from doesn't exceed to
        if (newSeconds >= toSeconds.value) {
            newSeconds = toSeconds.value - 1;
        }
        fromTimeString.value = secondsToTimeString(newSeconds);
    } else {
        // Ensure to doesn't go below from
        if (newSeconds <= fromSeconds.value) {
            newSeconds = fromSeconds.value + 1;
        }
        toTimeString.value = secondsToTimeString(newSeconds);
    }
    
    event.preventDefault();
}

function onDragEnd() {
    draggingHandle.value = null;
    
    // Remove global listeners
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
    document.removeEventListener('touchmove', onDragMove);
    document.removeEventListener('touchend', onDragEnd);
}

// Click on timeline to set nearest handle
function onTimelineClick(event) {
    if (draggingHandle.value) return; // Already dragging
    if (event.target.closest('.time-handle')) return; // Clicked on handle itself
    if (!timelineContainerEl.value) return;
    
    const rect = timelineContainerEl.value.getBoundingClientRect();
    const positionX = event.clientX - rect.left;
    const percentX = Math.max(0, Math.min(100, (positionX / rect.width) * 100));
    const clickedSeconds = Math.floor((percentX / 100) * 86400);
    
    // Determine which handle is closer
    const distanceToFrom = Math.abs(clickedSeconds - fromSeconds.value);
    const distanceToTo = Math.abs(clickedSeconds - toSeconds.value);
    
    if (distanceToFrom < distanceToTo) {
        // Move from handle
        let newSeconds = Math.max(0, Math.min(toSeconds.value - 1, clickedSeconds));
        fromTimeString.value = secondsToTimeString(newSeconds);
    } else {
        // Move to handle
        let newSeconds = Math.max(fromSeconds.value + 1, Math.min(86399, clickedSeconds));
        toTimeString.value = secondsToTimeString(newSeconds);
    }
}

// Format job name for display
function formatJobName(job) {
    if (job.DateFrom && job.DateTo) {
        return `${job.DateFrom.replace('T', ' ') + ' '} - ${job.DateTo.replace('T', ' ') + ' '}`;
    }
    if (job.Name) {
        return job.Name;
    }
    return 'Запись';
}

// Get download URL from PublicUrl
function getDownloadUrl(name) {
    const isLocalhost = window.location.hostname === 'localhost';
    const protocol = isLocalhost ? 'https://' : window.location.protocol;
    const baseUrl = props.url.startsWith('//') ? `${protocol}${props.url}` : props.url;
    return `${baseUrl}/downloads/${name}`;
}

async function startTranscode() {
    if (!isValidRange.value) {
        error.value = 'Неверный временной диапазон';
        return;
    }

    error.value = '';

    try {
        // Format date as yyyy_MM_d (no leading zero on day)
        const [year, month, day] = selectedDay.value.split('-');
        const dateFormatted = `${year}_${month}_${parseInt(day)}`;

        // Call transcode API with POST
        // Ensure URL has protocol to avoid CORS issues with protocol-relative URLs
        const isLocalhost = window.location.hostname === 'localhost';
        const protocol = isLocalhost ? 'https://' : window.location.protocol;
        const baseUrl = props.url.startsWith('//') ? `${protocol}${props.url}` : props.url;
        const transcodeUrl = `${baseUrl}/api/transcodeToUser`;



        const options = {
            method: 'POST',
            url: transcodeUrl,
            headers: { 'Content-Type': 'application/json' },
            data: {
                cam: props.cameraId,
                date: dateFormatted,
                from: fromSeconds.value,
                to: toSeconds.value
            }
        }

        const response = await axios.request(options)
        console.log(response);

        if (!response.status === 202) {
            throw new Error(`Ошибка запроса: ${response.data.message}`);
        }

        // Start SSE connection to monitor progress
        connectToSSE();
    } catch (err) {
        error.value = err.message;
        console.error('Transcode error:', err);
    }
}

function connectToSSE() {
    // Ensure URL has protocol to avoid CORS issues
    const isLocalhost = window.location.hostname === 'localhost';
    const protocol = isLocalhost ? 'https://' : window.location.protocol;
    const baseUrl = props.url.startsWith('//') ? `${protocol}${props.url}` : props.url;
    const sseUrl = `${baseUrl}/api/concatToCloudAllSSE`;
    eventSource = new EventSource(sseUrl);

    eventSource.onmessage = (event) => {
        try {
            const data = JSON.parse(event.data);

            // Filter jobs for this camera
            const cameraJobs = data.filter(job => {
                return job.Name?.includes(props.cameraId);
            });

            existingJobs.value = cameraJobs;
            


        } catch (err) {
            console.error('SSE parse error:', err);
        }
    };

    // eventSource.onerror = (err) => {
    //     console.error('SSE error:', err);
    //     error.value = 'Потеряно соединение с сервером';
    //     if (eventSource) {
    //         eventSource.close();
    //         eventSource = null;
    //     }
    // };
}

function close() {
    if (eventSource) {
        eventSource.close();
        eventSource = null;
    }
    emit('close');
}

onBeforeUnmount(() => {
    if (eventSource) {
        eventSource.close();
        eventSource = null;
    }
    document.removeEventListener('click', handleClickOutside);
    
    // Clean up drag listeners
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
    document.removeEventListener('touchmove', onDragMove);
    document.removeEventListener('touchend', onDragEnd);
});
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
    pointer-events: auto;
    opacity: 1 !important;
}

.modal-content {
    background: #1a1a1a;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    width: 520px;
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    animation: modalFadeIn 0.2s ease;
}

@keyframes modalFadeIn {
    from {
        opacity: 0;
        transform: scale(0.95);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #8ba3c7;
}

.close-btn {
    background: none;
    border: none;
    color: #fff;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.close-btn .mdi {
    font-size: 24px;
    line-height: 1;
}

.close-btn:hover {
    color: #8ba3c7;
    transform: scale(1.1);
}

.modal-body {
    padding: 24px;
    overflow-y: auto;
    flex: 1;
}

.existing-jobs-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.section-title {
    font-size: 14px;
    font-weight: 600;
    color: #8ba3c7;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.jobs-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 200px;
    overflow-y: auto;
}

.jobs-list::-webkit-scrollbar {
    width: 6px;
}

.jobs-list::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
}

.jobs-list::-webkit-scrollbar-thumb {
    background: rgba(139, 163, 199, 0.3);
    border-radius: 3px;
}

.jobs-list::-webkit-scrollbar-thumb:hover {
    background: rgba(139, 163, 199, 0.5);
}

.job-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    transition: all 0.2s;
}

.job-item:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(139, 163, 199, 0.3);
}

.job-processing {
    opacity: 0.7;
}

.job-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.job-name {
    font-size: 13px;
    color: #fff;
    font-weight: 500;
}

.job-details-mini {
    font-size: 11px;
    color: #888;
    display: flex;
    gap: 8px;
}

.job-status {
    color: #8ba3c7;
}

.job-download-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: #6b8bb3;
    color: #fff;
    border-radius: 6px;
    transition: all 0.2s;
    text-decoration: none;
}

.job-download-btn .mdi {
    font-size: 18px;
    line-height: 1;
}

.job-download-btn:hover {
    background: #8ba3c7;
    transform: translateY(-2px);
}

.section-divider {
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
    margin: 8px 0;
}

.time-selection {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.info-text {
    color: #aaa;
    font-size: 14px;
}

.time-inputs {
    display: flex;
    gap: 16px;
}

.input-group {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.input-group label {
    color: #aaa;
    font-size: 13px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.time-input-masked {
    padding: 10px 0;
    background: rgba(255, 255, 255, 0.05);
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    font-family: 'Courier New', monospace;
    text-align: center;
    transition: all 0.2s;
    letter-spacing: 1px;
}

.time-input-masked:focus {
    outline: none;
    border-color: #6b8bb3;
    background: rgba(107, 139, 179, 0.1);
}

.time-input-masked::placeholder {
    color: rgba(255, 255, 255, 0.2);
}

.date-selector-wrapper {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.date-label {
    color: #aaa;
    font-size: 13px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.date-selector {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
}

.date-nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    background: #1a2332;
    color: #9aa4b2;
    border: 1px solid #2a3547;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
}

.date-nav-btn .mdi {
    font-size: 20px;
    line-height: 1;
}

.date-nav-btn:hover:not(:disabled) {
    background: #243349;
    color: #e8edf3;
    border-color: #3a465a;
}

.date-nav-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.date-display-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    background: rgba(107, 139, 179, 0.1);
    border: 1px solid rgba(107, 139, 179, 0.2);
    border-radius: 6px;
    color: #8ba3c7;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.date-display-btn .mdi {
    font-size: 18px;
    line-height: 1;
}

.date-display-btn:hover {
    background: rgba(107, 139, 179, 0.15);
    border-color: rgba(107, 139, 179, 0.3);
}

.date-picker {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 8px;
    background: #1a2332;
    border: 1px solid #2a3547;
    border-radius: 6px;
    max-height: 240px;
    overflow-y: auto;
    z-index: 100;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    animation: datePickerFadeIn 0.2s ease;
}

@keyframes datePickerFadeIn {
    from {
        opacity: 0;
        transform: translateY(-8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.date-picker::-webkit-scrollbar {
    width: 6px;
}

.date-picker::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
}

.date-picker::-webkit-scrollbar-thumb {
    background: rgba(139, 163, 199, 0.3);
    border-radius: 3px;
}

.date-picker::-webkit-scrollbar-thumb:hover {
    background: rgba(139, 163, 199, 0.5);
}

.date-option {
    padding: 10px 16px;
    color: #e8edf3;
    cursor: pointer;
    transition: all 0.15s;
    font-size: 13px;
}

.date-option:hover {
    background: rgba(255, 255, 255, 0.05);
}

.date-option.active {
    background: rgba(107, 139, 179, 0.2);
    color: #8ba3c7;
    font-weight: 500;
    border-left: 3px solid #6b8bb3;
}

.segments-timeline {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.segments-container {
    position: relative;
    height: 24px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    overflow: visible;
    cursor: pointer;
}

.segment-bar {
    position: absolute;
    top: 0;
    height: 100%;
    background: #7a9abe;
    transition: opacity 0.2s;
    border-radius: 2px;
}

.selected-range {
    position: absolute;
    top: 0;
    height: 100%;
    background: rgba(139, 163, 199, 0.25);
    border: 1px solid #8ba3c7;
    border-radius: 2px;
    pointer-events: none;
    z-index: 1;
    box-shadow: inset 0 0 6px rgba(139, 163, 199, 0.3);
}

.time-handle {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 3;
    cursor: grab;
    user-select: none;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.time-handle:active {
    cursor: grabbing;
}

.handle-line {
    width: 2px;
    height: 36px;
    background: #8ba3c7;
    border-radius: 1px;
    box-shadow: 0 1px 4px rgba(139, 163, 199, 0.5);
    transition: all 0.2s;
}

.time-handle:hover .handle-line {
    background: #a3bbd4;
    box-shadow: 0 2px 8px rgba(139, 163, 199, 0.7);
}

.handle-grip {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 20px;
    height: 20px;
    background: #1a2332;
    border: 2px solid #8ba3c7;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8ba3c7;
    transition: all 0.2s;
}

.handle-grip .mdi {
    font-size: 14px;
    line-height: 1;
}

.time-handle:hover .handle-grip {
    background: #243349;
    border-color: #a3bbd4;
    color: #a3bbd4;
    transform: translate(-50%, -50%) scale(1.15);
}

.time-markers {
    display: flex;
    justify-content: space-between;
    padding: 0 4px;
    font-size: 11px;
    color: #888;
    font-family: 'Courier New', monospace;
}

.duration-display {
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 6px;
    font-weight: 600;
    color: #8ba3c7;
    font-size: 15px;
    text-align: center;
}

.warning-message {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #ffa500;
    font-size: 13px;
    padding: 12px;
    background: rgba(255, 165, 0, 0.1);
    border-radius: 6px;
    border: 1px solid rgba(255, 165, 0, 0.3);
}

.warning-message .mdi {
    font-size: 18px;
    flex-shrink: 0;
}

.error-message {
    color: #ff6b6b;
    font-size: 14px;
    padding: 12px;
    background: rgba(255, 107, 107, 0.1);
    border-radius: 6px;
    border: 1px solid rgba(255, 107, 107, 0.3);
}

.progress-section {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.progress-info {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.status-text {
    font-size: 16px;
    font-weight: 600;
    color: #8ba3c7;
}

.job-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
}

.detail-label {
    color: #888;
}

.detail-value {
    color: #fff;
    font-weight: 500;
}

.progress-bar {
    width: 100%;
    height: 8px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #6b8bb3, #8ba3c7);
    transition: width 0.3s ease;
}

.download-section {
    display: flex;
    justify-content: center;
}

.download-link {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: #6b8bb3;
    color: #fff;
    text-decoration: none;
    border-radius: 6px;
    font-weight: 600;
    transition: transform 0.2s, background 0.2s;
}

.download-link:hover {
    background: #8ba3c7;
    transform: translateY(-2px);
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px 24px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.btn {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-primary {
    background: #6b8bb3;
    color: #fff;
}

.btn-primary:hover:not(:disabled) {
    background: #8ba3c7;
    transform: translateY(-1px);
}

.btn-secondary {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
}

.btn-secondary:hover {
    background: rgba(255, 255, 255, 0.15);
}
</style>
